import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {CheckoutService} from './checkout.service';
import {ScanAndGoService} from '../scan-and-go/scan-and-go.service';
import {DashboardService} from '../dashboard.service';
import {HttpErrorResponse, HttpStatusCode} from '@angular/common/http';
import {animate, style, transition, trigger} from '@angular/animations';

@Component({
    selector: 'app-checkout',
    templateUrl: './checkout.component.html',
    styleUrls: ['./checkout.component.scss'],
    animations: [
        trigger('fadeSlideInOut', [
            transition(':enter', [
                style({opacity: 0, transform: 'translateY(80px)'}),
                animate('300ms', style({opacity: 1, transform: 'translateY(0)'})),
            ]),
        ])
    ]
})
export class CheckoutComponent implements OnInit, OnChanges {
    @Input() cards: any[] = [];

    public userHasCards = false;
    public userReachedCardLimit = false;
    public userIsAddingCard = false;
    public userNotMakeASelection = true;

    public providerCardIssue = false;
    public insufficientFounds = false;

    public recentIssuedCard: any[] = [];
    public recentIssuedCardNumber = '';

    @Output() qrCodeStep = new EventEmitter<number>();
    @Output() skipTerminalStep = new EventEmitter<boolean>();
    @Output() terminalStep = new EventEmitter<number>();
    @Output() cartStep = new EventEmitter<number>();
    @Output() selectedPaymentEv = new EventEmitter<any>();
    @Output() confirmChanges = new EventEmitter<boolean>();

    @Input() selectedPayment: any;
    @Input() selectedPaymentType = '';
    @Input() removedPayment: any;

    @Input() expressMode: boolean;
    @Input() loadDataAutomatically = false;
    @Input() enableOneTapPayment = true;
    @Input() setDefaultPaymentOnTap = false;
    @Input() showHeader = true;
    @Input() showConfirmButton = false;

    @Input() orderId: number;
    @Input() total: number;

    @ViewChild('conektaIframeContainer')
    conektaFrame: ElementRef<any>;

    isMobile: boolean;

    constructor(
        private checkoutService: CheckoutService,
        private scanAndGoService: ScanAndGoService,
        private dashboardService: DashboardService,
    ) {
    }

    ngOnInit() {
        if (this.loadDataAutomatically) {
            this.getCardsFromUser();
        }

        this.dashboardService.isMobile$.subscribe(value => {
            this.isMobile = value;
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        // console.log(changes);

        if (changes.hasOwnProperty('cards')) {
            const cards = changes.cards.currentValue;

            this.cards = cards;
            this.userHasCards = cards.length > 0;
            this.userReachedCardLimit = this.checkIfUserReachedCardLimit(cards.length);
        }
    }

    userContinue(): void {
        this.confirmChanges.emit(this.selectedPayment);
    }

    onCartStep(): void {
        this.cartStep.emit(2);
    }

    onQrGenerationStep(): void {
        // console.log('Qr step called');
        this.qrCodeStep.emit(5);
    }

    onSelectedPayment(event: any): void {
        const {type} = event;

        this.selectedPaymentType = type;
        this.selectedPayment = event;

        if (type === 'card') {
            const search = this.recentIssuedCard.find(card => card.id === event.id);

            if (search) {
                this.providerCardIssue = true;
                this.userNotMakeASelection = true;
                this.recentIssuedCardNumber = search.last4;

                return;
            }
        }

        this.userNotMakeASelection = false;
        this.providerCardIssue = false;

        this.selectedPaymentEv.emit(event);
    }

    onDeletedPayment(event: any): void {
        const newPayments: any[] =
            this.cards.filter(card => card.id !== event.id);

        this.cards = newPayments;
        this.selectedPayment = null;
        this.userNotMakeASelection = true;

        if (newPayments.length < 3) {
            this.userReachedCardLimit = false;
        }

        if (newPayments.length === 0) {
            this.userHasCards = false;
        }

        const findOnIssuedCards =
            this.recentIssuedCard.find(card => card.id === event.id);

        if (findOnIssuedCards) {
            this.recentIssuedCard = this.recentIssuedCard.filter(card => card !== findOnIssuedCards);
        }
    }

    makeOrderProcess(): void {
        const {type} = this.selectedPayment;

        if (type === 'terminal') {
            this.terminalStep.emit(5);
            return;
        }

        const orderId = this.orderId;
        const paymentId = this.selectedPayment.id;

        this.payOrder(orderId, paymentId);
    }

    private checkIfUserReachedCardLimit(total: number): boolean {
        return total === 3;
    }

    private getCardsFromUser(): void {
        this.checkoutService.getCustomerPayments().subscribe(
            response => this.onUserCardsSuccess(response),
            error => this.onUserCardsError(error)
        );
    }

    private onUserCardsSuccess(response: any): void {
        const {data} = response;
        const {data: cards, total} = data;

        this.cards = cards;
        this.userHasCards = total > 0;
        this.userReachedCardLimit = this.checkIfUserReachedCardLimit(total);
    }

    private onUserCardsError(error: Error): void {
        console.log('El usuario no tiene tarjetas');
        console.error(error);
    }

    private payOrder(orderId: any, paymentId: string): void {
        const body = {orderId, paymentId};

        this.checkoutService.payOrder(body).subscribe(
            response => this.onOrderPayResponse(response),
            error => this.onOrderPayError(error)
        );
    }

    private onOrderPayResponse(response: any): void {
        this.onQrGenerationStep();
    }

    private onOrderPayError(error: HttpErrorResponse): void {
        const {status} = error;

        if (status === HttpStatusCode.Conflict) {
            // Already paid order, notify to user
            this.onQrGenerationStep();
        }

        // Add card to view instance to be unable availability to process
        // order with this source again.
        if (status === HttpStatusCode.PaymentRequired) {
            this.recentIssuedCard.push(this.selectedPayment);
            this.recentIssuedCardNumber = this.selectedPayment.last4;
            this.providerCardIssue = true;
        }
    }

    onAddNewPayment(): void {
        this.userIsAddingCard = true;
        this.userNotMakeASelection = true;
    }

    onIsAddedCardOk(event: any): void {
        // console.log(event);

        this.userIsAddingCard = false;

        // Reload cards showed to user if isCardAddedOk returns true
        if (event) {
            this.getCardsFromUser();
        }
    }
}
